import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { quickAttendanceStudents } from '../data/mockData';

export default function ActionModal({ isOpen, onClose }) {
  const [step, setStep] = useState(1); // 1: Select, 2: Loading, 3: Success
  const [students, setStudents] = useState(quickAttendanceStudents);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) setStep(1);
  }, [isOpen]);

  const toggleAttendance = (id) => {
    setStudents(students.map(s => s.id === id ? { ...s, present: !s.present } : s));
  };

  const handleSubmit = () => {
    setStep(2);
    setTimeout(() => {
      setStep(3);
      setTimeout(() => {
        onClose();
      }, 2000);
    }, 1500);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={step === 1 ? onClose : undefined}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-900 border border-slate-700/50 rounded-2xl w-full max-w-md shadow-2xl shadow-blue-900/20 overflow-hidden"
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center bg-slate-800/30">
                <div className="flex items-center gap-2">
                  <SafeIcon icon="FiZap" className="text-bell" />
                  <h3 className="font-semibold text-chalk">Quick Action: Mark Attendance</h3>
                </div>
                {step === 1 && (
                  <button onClick={onClose} className="text-slate-400 hover:text-white">
                    <SafeIcon icon="FiX" />
                  </button>
                )}
              </div>

              {/* Content Area */}
              <div className="p-6 min-h-[300px] flex flex-col relative">
                
                {/* Step 1: Selection */}
                <AnimatePresence mode="wait">
                  {step === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="flex-1 flex flex-col"
                    >
                      <div className="mb-4">
                        <p className="text-sm text-slate-400">Class: <span className="text-principal font-medium">Computer Science 101</span></p>
                        <p className="text-xs text-slate-500 font-mono mt-1">Time: 01:00 PM - 02:30 PM</p>
                      </div>

                      <div className="space-y-2 mb-6 flex-1 overflow-y-auto custom-scrollbar pr-2">
                        {students.map((student) => (
                          <div key={student.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                            <div>
                              <p className="text-sm font-medium text-slate-200">{student.name}</p>
                              <p className="text-[10px] text-slate-500 uppercase">{student.grade}</p>
                            </div>
                            <button
                              onClick={() => toggleAttendance(student.id)}
                              className={`
                                relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none
                                ${student.present ? 'bg-principal' : 'bg-slate-600'}
                              `}
                            >
                              <span className={`
                                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                ${student.present ? 'translate-x-6' : 'translate-x-1'}
                              `} />
                            </button>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={handleSubmit}
                        className="w-full bg-principal text-white py-3 rounded-xl font-medium shadow-lg hover:bg-blue-500 transition-colors flex items-center justify-center gap-2"
                      >
                        Submit Attendance Records
                      </button>
                    </motion.div>
                  )}

                  {/* Step 2: Loading */}
                  {step === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 1.1 }}
                      className="flex-1 flex flex-col items-center justify-center absolute inset-0 bg-slate-900"
                    >
                      <div className="w-16 h-16 border-4 border-slate-700 border-t-principal rounded-full animate-spin mb-4"></div>
                      <p className="text-slate-300 font-medium">Syncing to secure ledger...</p>
                      <p className="text-xs text-slate-500 font-mono mt-2">Encrypting payload</p>
                    </motion.div>
                  )}

                  {/* Step 3: Success */}
                  {step === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex-1 flex flex-col items-center justify-center absolute inset-0 bg-slate-900"
                    >
                      <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-6 border border-green-500/30">
                        <SafeIcon icon="FiCheck" className="text-4xl text-green-400" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Operation Successful</h3>
                      <p className="text-slate-400 text-center text-sm">Attendance logs updated and synchronized across all modules.</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}